/**
 * カタログの検証。
 *
 * - `validateLegacyCatalog`: 移行前のカタログ原本(`tools/content-build/data/catalog/`)の検証。
 *   もと `tools/content-build/src/catalog.ts` の `validateCatalog` をそのまま移した(メッセージも同じ)。
 * - `validateCatalogDefs`: 科目・キャラクター・アイテム・ガチャ・回収(docs/19 §3・§6)の検証。
 *   旧規則のうち「教科ガチャ限定」は「1つのアイテムは高々1本の active なガチャに入る」に一般化し、
 *   trophy ガチャとチャレンジ由来の印(origin)の双方向一致は従来どおり。
 */
import {
  CATALOG_CHARACTERS_PATH,
  CATALOG_GACHAS_PATH,
  CATALOG_ITEMS_PATH,
  CATALOG_RETIREMENTS_PATH,
  CATALOG_SUBJECTS_PATH,
  GACHA_CURRENCIES,
  ITEM_EFFECT_TYPES,
  ITEM_ORIGINS,
  ITEM_SLOTS,
  RARITIES,
  type CatalogItem,
  type GachaDefinition,
  type GachaPoolEntry,
  type ItemEffect,
  type Rarity,
} from './legacy.js';
import {
  CHARACTER_STATUSES,
  GACHA_STATUSES,
  ITEM_STATUSES,
  RIGS,
  RIG_TYPES,
  SPECIAL_CURRENCIES,
  SUBJECT_STATUSES,
  type CharacterDef,
  type GachaDef,
  type ItemDef,
  type LocalizedText,
  type LocalizedWord,
  type RetirementDef,
  type SlotDef,
  type SubjectDef,
} from './defs.js';
import { appendTo, createIssues, type ValidationResult } from './result.js';

/** 排出率の合計がこの誤差内なら 1.0 とみなす。 */
const RATE_EPSILON = 1e-9;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

// ── 共通部品 ─────────────────────────────────────────────

function validateEffects(effects: unknown, where: string, errors: string[]): void {
  if (!Array.isArray(effects)) {
    errors.push(`${where}: effects が配列ではありません`);
    return;
  }
  for (const [i, effect] of (effects as ItemEffect[]).entries()) {
    if (!(ITEM_EFFECT_TYPES as readonly string[]).includes(effect.type)) {
      errors.push(
        `${where}.effects[${i}]: engine が知らない効果 ${String(effect.type)}` +
          `(有効: ${ITEM_EFFECT_TYPES.join(' / ')})`,
      );
    }
    if (typeof effect.value !== 'number' || !Number.isFinite(effect.value) || effect.value <= 0) {
      errors.push(`${where}.effects[${i}]: value は正の数が必要です`);
    }
  }
}

function validateOrigin(origin: unknown, where: string, errors: string[]): void {
  // 出どころ(docs/12 §3)。将来の装備ゲートがここを見て対象外を判断するので、
  // 綴り違いを配信前に止める(黙って `gacha` 扱いになるのがいちばん危ない)。
  if (origin !== undefined && !(ITEM_ORIGINS as readonly string[]).includes(origin as string)) {
    errors.push(
      `${where}.origin: 未知の出どころ ${String(origin)}(有効: ${ITEM_ORIGINS.join(' / ')})`,
    );
  }
}

/** `YYYY-MM` の形か(月は 01〜12)。 */
const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/** 月限定枠の `months`(docs/12 §3)。空配列・綴り違いは配信前に止める。 */
function validateMonths(months: readonly string[], where: string, errors: string[]): void {
  if (!Array.isArray(months) || months.length === 0) {
    errors.push(`${where}: 1件以上の YYYY-MM が必要です(恒常枠なら months ごと省く)`);
    return;
  }
  const seen = new Set<string>();
  for (const [i, month] of months.entries()) {
    if (typeof month !== 'string' || !MONTH_PATTERN.test(month)) {
      errors.push(`${where}[${i}]: YYYY-MM の形が必要です(${String(month)})`);
      continue;
    }
    if (seen.has(month)) errors.push(`${where}[${i}]: ${month} が重複しています`);
    seen.add(month);
  }
}

/** ガチャのうち、価格・通貨・排出率・重複変換の部分(新旧共通)。 */
function validateGachaEconomy(
  gacha: {
    readonly id: unknown;
    readonly currency: unknown;
    readonly cost: unknown;
    readonly cost10: unknown;
    readonly rarityRates?: Readonly<Record<string, unknown>>;
    readonly duplicateConvertCoins?: Readonly<Record<string, unknown>>;
  },
  where: string,
  errors: string[],
): void {
  if (typeof gacha.id !== 'string' || gacha.id === '') errors.push(`${where}: id が空です`);
  // 支払い通貨(docs/10 §4.3)。Functions が実際に引き落とす通貨なので打ち間違いを止める。
  if (!(GACHA_CURRENCIES as readonly string[]).includes(gacha.currency as string)) {
    errors.push(
      `${where}.currency: 未知の通貨 ${String(gacha.currency)}` +
        `(有効: ${GACHA_CURRENCIES.join(' / ')})`,
    );
  }
  for (const key of ['cost', 'cost10'] as const) {
    const value = gacha[key];
    if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
      errors.push(`${where}.${key}: 正の整数が必要です`);
    }
  }

  // 排出率: 4レアリティすべてを持ち、合計 1.0。
  let rateSum = 0;
  for (const rarity of RARITIES) {
    const rate = gacha.rarityRates?.[rarity];
    if (typeof rate !== 'number' || !Number.isFinite(rate) || rate < 0) {
      errors.push(`${where}.rarityRates.${rarity}: 0以上の数値が必要です`);
      continue;
    }
    rateSum += rate;
  }
  if (Math.abs(rateSum - 1) > RATE_EPSILON) {
    errors.push(`${where}.rarityRates: 合計が 1.0 ではありません(${rateSum})`);
  }

  // 重複変換コイン: 4レアリティすべて必須。
  for (const rarity of RARITIES) {
    const coins = gacha.duplicateConvertCoins?.[rarity];
    if (typeof coins !== 'number' || !Number.isInteger(coins) || coins < 0) {
      errors.push(`${where}.duplicateConvertCoins.${rarity}: 0以上の整数が必要です`);
    }
  }
}

/**
 * プールの参照・重複・重み・月限定枠を見て、**恒常枠だけ**のレアリティ内訳を返す
 * (月限定枠は当月しか出ないので、抽選が成立するかは恒常枠だけで判定する。docs/12 §3)。
 */
function validatePool<I extends { readonly rarity: Rarity }>(
  pool: readonly GachaPoolEntry[],
  where: string,
  itemsById: ReadonlyMap<string, I>,
  errors: string[],
  onItem?: (item: I, index: number) => void,
): Map<Rarity, number> {
  const seen = new Set<string>();
  const rarityCounts = new Map<Rarity, number>();
  for (const [i, entry] of pool.entries()) {
    const item = itemsById.get(entry.itemId);
    if (!item) {
      errors.push(`${where}.pool[${i}]: アイテム ${entry.itemId} がカタログにありません`);
      continue;
    }
    if (seen.has(entry.itemId)) {
      errors.push(`${where}.pool[${i}]: アイテム ${entry.itemId} が重複しています`);
    }
    seen.add(entry.itemId);
    onItem?.(item, i);
    if (typeof entry.weight !== 'number' || !Number.isFinite(entry.weight) || entry.weight <= 0) {
      errors.push(`${where}.pool[${i}]: weight は正の数が必要です`);
    }
    if (entry.months === undefined) {
      rarityCounts.set(item.rarity, (rarityCounts.get(item.rarity) ?? 0) + 1);
      continue;
    }
    validateMonths(entry.months, `${where}.pool[${i}].months`, errors);
  }
  return rarityCounts;
}

/** 排出率が正のレアリティは必ず恒常枠に実体がないと抽選が破綻する。 */
function validateRatesCovered(
  rarityRates: Readonly<Record<string, unknown>> | undefined,
  rarityCounts: ReadonlyMap<Rarity, number>,
  where: string,
  errors: string[],
): void {
  for (const rarity of RARITIES) {
    const rate = (rarityRates?.[rarity] as number | undefined) ?? 0;
    if (rate > 0 && (rarityCounts.get(rarity) ?? 0) === 0) {
      errors.push(
        `${where}: レアリティ ${rarity} の排出率が ${rate} なのに恒常枠に1件もありません`,
      );
    }
  }
}

function validateGuaranteeRarity(
  count: unknown,
  rarity: unknown,
  rarityKey: string,
  rarityCounts: ReadonlyMap<Rarity, number>,
  where: string,
  errors: string[],
): void {
  if (!Number.isInteger(count) || (count as number) <= 1) {
    errors.push(`${where}.guarantee.count: 2以上の整数が必要です`);
  }
  if (!(RARITIES as readonly string[]).includes(rarity as string)) {
    errors.push(`${where}.guarantee.${rarityKey}: 未知のレアリティ ${String(rarity)}`);
    return;
  }
  const minIndex = RARITIES.indexOf(rarity as Rarity);
  const available = RARITIES.slice(minIndex).some((r) => (rarityCounts.get(r) ?? 0) > 0);
  if (!available) {
    errors.push(`${where}.guarantee: ${String(rarity)} 以上のアイテムがプールにありません`);
  }
}

function validatePeriod(period: unknown, where: string, errors: string[]): void {
  if (!isRecord(period) || !('from' in period) || !('to' in period)) {
    errors.push(`${where}.period: { from, to } が必要です(無期限は null)`);
  }
}

/**
 * チャレンジ由来の印(docs/12 §3)の双方向一致。
 * trophy ガチャから出るものは全部 `origin: "challenge"`、逆に `challenge` は trophy ガチャからしか出ない。
 */
function validateTrophyOrigin(
  items: readonly { readonly id: string; readonly origin?: string }[],
  gachas: readonly {
    readonly id: string;
    readonly currency: string;
    readonly pool?: readonly GachaPoolEntry[];
  }[],
  errors: string[],
): void {
  const gachaIdsByItem = new Map<string, string[]>();
  for (const gacha of gachas) {
    for (const entry of gacha.pool ?? []) {
      appendTo(gachaIdsByItem, entry.itemId, gacha.id);
    }
  }
  const trophyGachaIds = new Set(
    gachas.filter((gacha) => gacha.currency === 'trophy').map((gacha) => gacha.id),
  );
  for (const item of items) {
    const from = gachaIdsByItem.get(item.id) ?? [];
    const fromTrophy = from.some((id) => trophyGachaIds.has(id));
    if (item.origin === 'challenge' && !fromTrophy) {
      errors.push(
        `${CATALOG_ITEMS_PATH}: ${item.id} は origin=challenge なのに ` +
          `トロフィーガチャから出ません(${from.join(' / ') || 'どこからも出ません'})`,
      );
    }
    if (item.origin !== 'challenge' && fromTrophy) {
      errors.push(
        `${CATALOG_ITEMS_PATH}: ${item.id} はトロフィーガチャから出るのに ` +
          `origin=challenge がありません(装備ゲートの対象外にできない)`,
      );
    }
  }
}

// ── 移行前カタログ(原本)────────────────────────────────

export interface LegacyCatalogData {
  readonly items: readonly CatalogItem[];
  readonly gachas: readonly GachaDefinition[];
}

function validateLegacyItem(item: CatalogItem, where: string, errors: string[]): void {
  if (typeof item.id !== 'string' || item.id === '') {
    errors.push(`${where}: id が空です`);
  }
  if (!(ITEM_SLOTS as readonly string[]).includes(item.slot)) {
    errors.push(`${where}: 未知のスロット ${String(item.slot)}`);
  }
  if (!(RARITIES as readonly string[]).includes(item.rarity)) {
    errors.push(`${where}: 未知のレアリティ ${String(item.rarity)}`);
  }
  if (!isRecord(item.name) || typeof item.name.ja !== 'string' || item.name.ja === '') {
    errors.push(`${where}: name.ja が必要です`);
  }
  // アート未整備のあいだ emoji は必須(UI が空表示にならないようにする)。
  if (typeof item.emoji !== 'string' || item.emoji === '') {
    errors.push(`${where}: emoji が必要です(アート差し替え前の暫定表現)`);
  }
  validateOrigin(item.origin, where, errors);
  if (!Array.isArray(item.effects)) {
    errors.push(`${where}: effects が配列ではありません`);
    return;
  }
  validateEffects(item.effects, where, errors);
  if (!Array.isArray(item.services) || item.services.length === 0) {
    errors.push(`${where}: services が空です`);
  }
}

function validateLegacyGacha(
  gacha: GachaDefinition,
  where: string,
  itemsById: ReadonlyMap<string, CatalogItem>,
  errors: string[],
): void {
  validateGachaEconomy(gacha, where, errors);
  if (!Array.isArray(gacha.pool) || gacha.pool.length === 0) {
    errors.push(`${where}.pool: 1件以上必要です`);
    return;
  }
  const rarityCounts = validatePool(gacha.pool, where, itemsById, errors);
  validateRatesCovered(gacha.rarityRates, rarityCounts, where, errors);

  const guarantee = gacha.guarantee as unknown;
  if (!isRecord(guarantee)) {
    errors.push(`${where}.guarantee: { count, minRarity } が必要です`);
  } else {
    validateGuaranteeRarity(
      guarantee.count,
      guarantee.minRarity,
      'minRarity',
      rarityCounts,
      where,
      errors,
    );
  }
  validatePeriod(gacha.period, where, errors);
}

/** 移行前カタログ全体の整合を検証してエラー一覧を返す(空なら OK)。 */
export function validateLegacyCatalog(catalog: LegacyCatalogData): readonly string[] {
  const errors: string[] = [];

  const itemsById = new Map<string, CatalogItem>();
  for (const [i, item] of catalog.items.entries()) {
    const where = `${CATALOG_ITEMS_PATH}[${i}]`;
    validateLegacyItem(item, where, errors);
    if (itemsById.has(item.id)) errors.push(`${where}: アイテムIDが重複: ${item.id}`);
    itemsById.set(item.id, item);
  }

  const gachaIds = new Set<string>();
  for (const [i, gacha] of catalog.gachas.entries()) {
    const where = `${CATALOG_GACHAS_PATH}[${i}]`;
    if (gachaIds.has(gacha.id)) errors.push(`${where}: ガチャIDが重複: ${gacha.id}`);
    gachaIds.add(gacha.id);
    validateLegacyGacha(gacha, where, itemsById, errors);
  }

  // どのガチャからも出ないアイテムは入手不能なので警告ではなくエラーにする。
  const obtainable = new Set<string>();
  for (const gacha of catalog.gachas) {
    for (const entry of gacha.pool ?? []) obtainable.add(entry.itemId);
  }
  for (const item of catalog.items) {
    if (!obtainable.has(item.id)) {
      errors.push(`${CATALOG_ITEMS_PATH}: ${item.id} はどのガチャからも出ません`);
    }
  }

  // モジュール限定アイテム(docs/10 §4.3)。typing 以外のガチャのアイテムは、そのガチャでしか出ない。
  const gachaIdsByItem = new Map<string, string[]>();
  for (const gacha of catalog.gachas) {
    for (const entry of gacha.pool ?? []) {
      appendTo(gachaIdsByItem, entry.itemId, gacha.id);
    }
  }
  for (const gacha of catalog.gachas) {
    if (gacha.currency === 'typing') continue;
    for (const entry of gacha.pool ?? []) {
      const others = (gachaIdsByItem.get(entry.itemId) ?? []).filter((id) => id !== gacha.id);
      if (others.length > 0) {
        errors.push(
          `${CATALOG_GACHAS_PATH}: ${gacha.id} 限定のはずの ${entry.itemId} が ` +
            `${others.join(' / ')} からも出ます`,
        );
      }
    }
  }

  validateTrophyOrigin(catalog.items, catalog.gachas, errors);
  return errors;
}

// ── 科目・キャラクター・アイテム・ガチャ・回収(docs/19)────────────

export interface CatalogDefs {
  readonly subjects: readonly SubjectDef[];
  readonly characters: readonly CharacterDef[];
  readonly items: readonly ItemDef[];
  readonly gachas: readonly GachaDef[];
  readonly retirements: readonly RetirementDef[];
}

/** 表示名(`ja` 必須。docs/19 §8「表示名は未設定なら公開時の検証でエラー」)。 */
function validateLocalized(
  value: LocalizedText | undefined,
  where: string,
  errors: string[],
): void {
  if (!isRecord(value) || !isNonEmptyString(value.ja)) {
    errors.push(`${where}: name.ja(日本語の表示名)が必要です`);
    return;
  }
  for (const [locale, text] of Object.entries(value)) {
    if (!isNonEmptyString(text)) errors.push(`${where}: name.${locale} が空です`);
  }
}

function validateStatus(
  value: unknown,
  allowed: readonly string[],
  where: string,
  errors: string[],
): void {
  if (!allowed.includes(value as string)) {
    errors.push(`${where}.status: 未知の状態 ${String(value)}(有効: ${allowed.join(' / ')})`);
  }
}

function indexById<T extends { readonly id: string }>(
  list: readonly T[],
  path: string,
  label: string,
  errors: string[],
): Map<string, T> {
  const map = new Map<string, T>();
  for (const [i, entry] of list.entries()) {
    if (!isNonEmptyString(entry.id)) {
      errors.push(`${path}[${i}]: id が空です`);
      continue;
    }
    if (map.has(entry.id)) errors.push(`${path}[${i}]: ${label}IDが重複: ${entry.id}`);
    map.set(entry.id, entry);
  }
  return map;
}

function validateSubject(
  subject: SubjectDef,
  where: string,
  charactersById: ReadonlyMap<string, CharacterDef>,
  errors: string[],
  warnings: string[],
): void {
  validateLocalized(subject.name, where, errors);
  if (!(GACHA_CURRENCIES as readonly string[]).includes(subject.currency)) {
    errors.push(`${where}.currency: 未知の通貨 ${String(subject.currency)}`);
  } else if (SPECIAL_CURRENCIES.includes(subject.currency)) {
    errors.push(`${where}.currency: ${subject.currency} は特別通貨なので科目の通貨にできません`);
  } else if (subject.currency !== subject.id) {
    warnings.push(`${where}.currency: 通貨 ${subject.currency} が科目ID ${subject.id} と違います`);
  }
  const character = charactersById.get(subject.characterId);
  if (!character) {
    errors.push(`${where}.characterId: キャラクター ${String(subject.characterId)} がありません`);
  } else if (character.status === 'retired') {
    errors.push(`${where}.characterId: キャラクター ${character.id} は廃止済みです`);
  }
  if (!isNonEmptyString(subject.color)) errors.push(`${where}.color: 色が必要です`);
  if (!isNonEmptyString(subject.icon)) errors.push(`${where}.icon: アイコンが必要です`);
  if (typeof subject.order !== 'number' || !Number.isFinite(subject.order)) {
    errors.push(`${where}.order: 数値が必要です`);
  }
  validateStatus(subject.status, SUBJECT_STATUSES, where, errors);
}

function validateWords(
  words: readonly LocalizedWord[] | undefined,
  where: string,
  errors: string[],
): void {
  if (!Array.isArray(words)) {
    errors.push(`${where}: 配列が必要です`);
    return;
  }
  const seen = new Set<string>();
  for (const [i, word] of words.entries()) {
    if (!isNonEmptyString(word.id)) errors.push(`${where}[${i}]: id が空です`);
    else if (seen.has(word.id)) errors.push(`${where}[${i}]: id ${word.id} が重複しています`);
    else seen.add(word.id);
    if (!isRecord(word.text) || !isNonEmptyString(word.text.ja)) {
      errors.push(`${where}[${i}]: text.ja が必要です`);
    }
  }
}

function validateCharacterShape(character: CharacterDef, where: string, errors: string[]): void {
  validateLocalized(character.name, where, errors);
  validateStatus(character.status, CHARACTER_STATUSES, where, errors);
  if (!(RIG_TYPES as readonly string[]).includes(character.rigType)) {
    errors.push(`${where}.rigType: 未知のリグ ${String(character.rigType)}`);
    return;
  }
  const rig = RIGS[character.rigType];
  const rigSlots = new Map(rig.slots.map((slot) => [slot.id, slot]));

  if (!Array.isArray(character.slots) || character.slots.length === 0) {
    errors.push(`${where}.slots: 1枠以上必要です`);
  } else {
    const seen = new Set<string>();
    for (const [i, slot] of character.slots.entries()) {
      const at = `${where}.slots[${i}]`;
      const known = rigSlots.get(slot.id);
      if (!known) {
        errors.push(
          `${at}: リグ ${rig.type} に枠 ${String(slot.id)} はありません` +
            `(有効: ${rig.slots.map((s) => s.id).join(' / ')})`,
        );
        continue;
      }
      if (seen.has(slot.id)) errors.push(`${at}: 枠 ${slot.id} が重複しています`);
      seen.add(slot.id);
      validateLocalized(slot.name, at, errors);
      // base / equip はリグが決める(素体はガチャで出さない。docs/19 §8)。
      if (slot.kind !== known.kind) {
        errors.push(`${at}.kind: リグ ${rig.type} の ${slot.id} は ${known.kind} です`);
      }
      if (typeof slot.required !== 'boolean') errors.push(`${at}.required: 真偽値が必要です`);
      if (slot.required && slot.kind !== 'base') {
        errors.push(`${at}.required: 外せない枠は素体(base)だけです`);
      }
    }
  }

  if (rig.usesBases) {
    if (!Array.isArray(character.bases) || character.bases.length === 0) {
      errors.push(`${where}.bases: リグ ${rig.type} は素体(bases)が1件以上必要です`);
    } else {
      const seen = new Set<string>();
      for (const [i, base] of character.bases.entries()) {
        const at = `${where}.bases[${i}]`;
        if (!isNonEmptyString(base.id)) errors.push(`${at}: id が空です`);
        else if (seen.has(base.id)) errors.push(`${at}: id ${base.id} が重複しています`);
        else seen.add(base.id);
        validateLocalized(base.name, at, errors);
        if (!isNonEmptyString(base.emoji)) errors.push(`${at}.emoji: 絵文字が必要です`);
        if (!isNonEmptyString(base.color)) errors.push(`${at}.color: 色が必要です`);
      }
    }
  } else if (character.bases !== undefined) {
    errors.push(`${where}.bases: リグ ${rig.type} は素体(bases)を持ちません`);
  }

  if (!isRecord(character.nameParts)) {
    errors.push(`${where}.nameParts: { prefixes, nouns } が必要です`);
  } else {
    validateWords(character.nameParts.prefixes, `${where}.nameParts.prefixes`, errors);
    validateWords(character.nameParts.nouns, `${where}.nameParts.nouns`, errors);
    // 保存する名前は部品の id の並び(docs/04 §5.1.4)。前と後ろで同じ id を使うと
    // 「どちらの部品か」を表記で取り違えるので、両方をまたいでも一意にする。
    if (Array.isArray(character.nameParts.prefixes) && Array.isArray(character.nameParts.nouns)) {
      const prefixIds = new Set(character.nameParts.prefixes.map((word) => word.id));
      for (const [i, word] of character.nameParts.nouns.entries()) {
        if (isNonEmptyString(word.id) && prefixIds.has(word.id)) {
          errors.push(`${where}.nameParts.nouns[${i}]: id ${word.id} が prefixes と重複しています`);
        }
      }
    }
  }
}

/**
 * 外せない枠(required)には、全員が最初から持っている starter が1件以上いる
 * (無いと、どの子も最初の見た目を作れない。docs/19 §3・§8)。
 */
function validateRequiredStarters(
  character: CharacterDef,
  where: string,
  items: readonly ItemDef[],
  errors: string[],
): void {
  for (const slot of character.slots ?? []) {
    if (!slot.required) continue;
    const hasStarter = items.some(
      (item) =>
        item.characterId === character.id &&
        item.slot === slot.id &&
        item.starter === true &&
        item.status === 'active',
    );
    if (!hasStarter) {
      errors.push(
        `${where}.slots.${slot.id}: 外せない枠なので starter のアイテムが1件以上必要です`,
      );
    }
  }
}

function slotOf(character: CharacterDef | undefined, slotId: string): SlotDef | undefined {
  return character?.slots?.find((slot) => slot.id === slotId);
}

function validateDefaultLook(
  character: CharacterDef,
  where: string,
  itemsById: ReadonlyMap<string, ItemDef>,
  errors: string[],
): void {
  const look = character.defaultLook;
  if (!isRecord(look) || !isRecord(look.equipped)) {
    errors.push(`${where}.defaultLook: { equipped } が必要です`);
    return;
  }
  if (look.base !== undefined) {
    const bases = character.bases ?? [];
    if (!bases.some((base) => base.id === look.base)) {
      errors.push(`${where}.defaultLook.base: 素体 ${String(look.base)} がありません`);
    }
  }
  for (const [slotId, itemId] of Object.entries(look.equipped)) {
    const at = `${where}.defaultLook.equipped.${slotId}`;
    if (!slotOf(character, slotId)) {
      errors.push(`${at}: 枠 ${slotId} はこのキャラクターにありません`);
      continue;
    }
    const item = itemsById.get(itemId);
    if (!item) {
      errors.push(`${at}: アイテム ${String(itemId)} がありません`);
      continue;
    }
    if (item.characterId !== character.id) {
      errors.push(`${at}: アイテム ${item.id} は ${item.characterId} 専用です`);
    }
    if (item.slot !== slotId) errors.push(`${at}: アイテム ${item.id} の枠は ${item.slot} です`);
    // 初期の見た目は全員が持っているもの(starter)か空だけ(docs/19 §3)。
    if (item.starter !== true) {
      errors.push(
        `${at}: アイテム ${item.id} は starter ではありません(初期の見た目は starter か空)`,
      );
    }
    if (item.status !== 'active') errors.push(`${at}: アイテム ${item.id} は廃止済みです`);
  }
  for (const slot of character.slots ?? []) {
    if (slot.required && look.equipped[slot.id] === undefined) {
      errors.push(
        `${where}.defaultLook.equipped.${slot.id}: 外せない枠なので初期の見た目が必要です`,
      );
    }
  }
}

function validateItemDef(
  item: ItemDef,
  where: string,
  charactersById: ReadonlyMap<string, CharacterDef>,
  errors: string[],
): void {
  if (!(RARITIES as readonly string[]).includes(item.rarity)) {
    errors.push(`${where}: 未知のレアリティ ${String(item.rarity)}`);
  }
  validateLocalized(item.name, where, errors);
  validateOrigin(item.origin, where, errors);
  validateEffects(item.effects, where, errors);
  if (!Array.isArray(item.services) || item.services.length === 0) {
    errors.push(`${where}: services が空です`);
  }
  validateStatus(item.status, ITEM_STATUSES, where, errors);
  if (item.starter !== undefined && typeof item.starter !== 'boolean') {
    errors.push(`${where}.starter: 真偽値が必要です`);
  }

  const character = charactersById.get(item.characterId);
  if (!character) {
    errors.push(`${where}.characterId: キャラクター ${String(item.characterId)} がありません`);
    return;
  }
  const slot = slotOf(character, item.slot);
  if (!slot) {
    errors.push(
      `${where}.slot: 枠 ${String(item.slot)} はキャラクター ${character.id} にありません`,
    );
  } else if (item.starter === true && slot.kind !== 'base') {
    errors.push(`${where}.starter: starter は素体(base)の枠だけです(${slot.id} は ${slot.kind})`);
  }
  // 素体(base)の枠は starter(最初から持っている)か、ガチャで集めるもの(2026-10-24 改定。docs/19 §3・§8)。

  const rig = RIGS[character.rigType] as (typeof RIGS)[keyof typeof RIGS] | undefined;
  const art = item.art as ItemDef['art'] | undefined;
  if (!isRecord(art)) {
    errors.push(`${where}.art: 絵の定義が必要です`);
    return;
  }
  if (rig && art.kind !== rig.artKind) {
    errors.push(`${where}.art.kind: リグ ${rig.type} のアイテムは ${rig.artKind} が必要です`);
  }
  if (art.kind === 'emoji') {
    if (!isNonEmptyString(art.emoji)) errors.push(`${where}.art.emoji: 絵文字が必要です`);
  } else if (art.kind === 'atlas') {
    for (const key of ['sheet', 'atlas', 'icon'] as const) {
      if (!isNonEmptyString(art[key])) errors.push(`${where}.art.${key}: パスが必要です`);
    }
    for (const [i, companion] of (art.companions ?? []).entries()) {
      const at = `${where}.art.companions[${i}]`;
      if (rig && !rig.companionSlots.includes(companion.slot)) {
        errors.push(`${at}.slot: 付属レイヤー ${String(companion.slot)} は使えません`);
      }
      if (!isNonEmptyString(companion.sheet) || !isNonEmptyString(companion.atlas)) {
        errors.push(`${at}: sheet と atlas が必要です`);
      }
    }
  } else {
    errors.push(`${where}.art.kind: 未知の種類 ${String((art as { kind?: unknown }).kind)}`);
  }
}

function validateGachaDef(
  gacha: GachaDef,
  where: string,
  subjectsById: ReadonlyMap<string, SubjectDef>,
  charactersById: ReadonlyMap<string, CharacterDef>,
  itemsById: ReadonlyMap<string, ItemDef>,
  errors: string[],
  warnings: string[],
): void {
  validateLocalized(gacha.name, where, errors);
  validateGachaEconomy(gacha, where, errors);
  validateStatus(gacha.status, GACHA_STATUSES, where, errors);

  const subject = subjectsById.get(gacha.subjectId);
  if (!subject) {
    errors.push(`${where}.subjectId: 科目 ${String(gacha.subjectId)} がありません`);
  } else if (gacha.currency !== subject.currency && !SPECIAL_CURRENCIES.includes(gacha.currency)) {
    errors.push(
      `${where}.currency: 科目 ${subject.id} の通貨は ${subject.currency} です(${gacha.currency})`,
    );
  }
  const character = charactersById.get(gacha.characterId);
  if (!character) {
    errors.push(`${where}.characterId: キャラクター ${String(gacha.characterId)} がありません`);
  } else if (subject && subject.characterId !== character.id && gacha.status === 'active') {
    warnings.push(
      `${where}.characterId: 科目 ${subject.id} のキャラクターは ${subject.characterId} です(ガチャは ${character.id})`,
    );
  }

  if (!Array.isArray(gacha.pool) || gacha.pool.length === 0) {
    errors.push(`${where}.pool: 1件以上必要です`);
    return;
  }
  const rarityCounts = validatePool(gacha.pool, where, itemsById, errors, (item, i) => {
    const at = `${where}.pool[${i}]`;
    // プールは同じキャラクターの equip 枠と、starter でない base 枠のアイテムだけ。
    // starter を含めない(docs/19 §3。2026-10-24 改定)。
    if (item.characterId !== gacha.characterId) {
      errors.push(`${at}: アイテム ${item.id} は ${item.characterId} 専用です`);
    }
    if (item.starter === true) errors.push(`${at}: starter のアイテム ${item.id} は入れられません`);
    if (item.status !== 'active' && gacha.status !== 'retired') {
      errors.push(`${at}: 廃止済みのアイテム ${item.id} は入れられません`);
    }
  });
  validateRatesCovered(gacha.rarityRates, rarityCounts, where, errors);

  const guarantee = gacha.guarantee as unknown;
  if (!isRecord(guarantee)) {
    errors.push(`${where}.guarantee: { rarity, count } が必要です`);
  } else {
    validateGuaranteeRarity(
      guarantee.count,
      guarantee.rarity,
      'rarity',
      rarityCounts,
      where,
      errors,
    );
  }
  validatePeriod(gacha.period, where, errors);
}

function validateRetirement(
  retirement: RetirementDef,
  where: string,
  itemsById: ReadonlyMap<string, ItemDef>,
  retiredBy: Map<string, string>,
  errors: string[],
  warnings: string[],
): void {
  if (!isNonEmptyString(retirement.reason)) errors.push(`${where}.reason: 理由が必要です`);
  const refund = retirement.refund as RetirementDef['refund'] | undefined;
  if (!isRecord(refund)) {
    errors.push(`${where}.refund: { currency, amountPerItem } が必要です`);
  } else {
    if (!(GACHA_CURRENCIES as readonly string[]).includes(refund.currency)) {
      errors.push(`${where}.refund.currency: 未知の通貨 ${String(refund.currency)}`);
    }
    if (!Number.isInteger(refund.amountPerItem) || refund.amountPerItem < 0) {
      errors.push(`${where}.refund.amountPerItem: 0以上の整数が必要です`);
    }
  }
  if (!Array.isArray(retirement.itemIds) || retirement.itemIds.length === 0) {
    errors.push(`${where}.itemIds: 1件以上必要です`);
    return;
  }
  for (const [i, itemId] of retirement.itemIds.entries()) {
    const at = `${where}.itemIds[${i}]`;
    const item = itemsById.get(itemId);
    if (!item) {
      errors.push(`${at}: アイテム ${String(itemId)} がありません`);
      continue;
    }
    // 1アイテムを2つの回収表に載せると二重返金になる。
    const previous = retiredBy.get(itemId);
    if (previous !== undefined) {
      errors.push(`${at}: アイテム ${itemId} は回収 ${previous} にも載っています(二重返金)`);
    }
    retiredBy.set(itemId, retirement.id);
    if (item.status !== 'retired') {
      warnings.push(`${at}: 回収するアイテム ${itemId} の status が retired ではありません`);
    }
    if (item.starter === true) errors.push(`${at}: starter のアイテム ${itemId} は回収できません`);
  }
}

/** 科目・キャラクター・アイテム・ガチャ・回収をまとめて検証する(docs/19 §3・§6)。 */
export function validateCatalogDefs(defs: CatalogDefs): ValidationResult {
  const { errors, warnings, result } = createIssues();

  const subjectsById = indexById(defs.subjects, CATALOG_SUBJECTS_PATH, '科目', errors);
  const charactersById = indexById(
    defs.characters,
    CATALOG_CHARACTERS_PATH,
    'キャラクター',
    errors,
  );
  const itemsById = indexById(defs.items, CATALOG_ITEMS_PATH, 'アイテム', errors);
  indexById(defs.gachas, CATALOG_GACHAS_PATH, 'ガチャ', errors);
  indexById(defs.retirements, CATALOG_RETIREMENTS_PATH, '回収', errors);

  for (const [i, character] of defs.characters.entries()) {
    validateCharacterShape(character, `${CATALOG_CHARACTERS_PATH}[${i}](${character.id})`, errors);
  }
  for (const [i, subject] of defs.subjects.entries()) {
    validateSubject(
      subject,
      `${CATALOG_SUBJECTS_PATH}[${i}](${subject.id})`,
      charactersById,
      errors,
      warnings,
    );
  }
  for (const [i, item] of defs.items.entries()) {
    validateItemDef(item, `${CATALOG_ITEMS_PATH}[${i}](${item.id})`, charactersById, errors);
  }
  for (const [i, character] of defs.characters.entries()) {
    validateDefaultLook(
      character,
      `${CATALOG_CHARACTERS_PATH}[${i}](${character.id})`,
      itemsById,
      errors,
    );
    validateRequiredStarters(
      character,
      `${CATALOG_CHARACTERS_PATH}[${i}](${character.id})`,
      defs.items,
      errors,
    );
  }
  for (const [i, gacha] of defs.gachas.entries()) {
    validateGachaDef(
      gacha,
      `${CATALOG_GACHAS_PATH}[${i}](${gacha.id})`,
      subjectsById,
      charactersById,
      itemsById,
      errors,
      warnings,
    );
  }
  const retiredBy = new Map<string, string>();
  for (const [i, retirement] of defs.retirements.entries()) {
    validateRetirement(
      retirement,
      `${CATALOG_RETIREMENTS_PATH}[${i}](${retirement.id})`,
      itemsById,
      retiredBy,
      errors,
      warnings,
    );
  }

  // 1つのアイテムは高々1本の active なガチャに入る(旧「教科ガチャ限定」の一般化)。
  const activeGachasByItem = new Map<string, string[]>();
  const liveGachasByItem = new Map<string, GachaDef[]>();
  for (const gacha of defs.gachas) {
    for (const entry of gacha.pool ?? []) {
      if (gacha.status === 'active') {
        appendTo(activeGachasByItem, entry.itemId, gacha.id);
      }
      if (gacha.status !== 'retired') {
        appendTo(liveGachasByItem, entry.itemId, gacha);
      }
    }
  }
  for (const [itemId, gachaIds] of activeGachasByItem) {
    if (gachaIds.length > 1) {
      errors.push(
        `${CATALOG_GACHAS_PATH}: アイテム ${itemId} が複数の active なガチャに入っています(${gachaIds.join(' / ')})`,
      );
    }
  }

  // 入手できないアイテム。starter は全員が所持扱い、廃止済みは入手できなくてよい。
  for (const item of defs.items) {
    if (item.starter === true || item.status !== 'active') continue;
    const live = liveGachasByItem.get(item.id) ?? [];
    if (live.length === 0) {
      errors.push(`${CATALOG_ITEMS_PATH}: ${item.id} はどのガチャからも出ません`);
    } else if (!live.some((gacha) => gacha.status === 'active')) {
      warnings.push(`${CATALOG_ITEMS_PATH}: ${item.id} は休止中のガチャからしか出ません`);
    }
  }

  // チャレンジ由来の印(従来どおり。廃止したガチャは対象外)。
  validateTrophyOrigin(
    defs.items,
    defs.gachas.filter((gacha) => gacha.status !== 'retired'),
    errors,
  );

  // 使われていない参照の警告(公開は可能)。
  for (const character of defs.characters) {
    if (
      character.status === 'active' &&
      !defs.subjects.some((s) => s.characterId === character.id)
    ) {
      warnings.push(`${CATALOG_CHARACTERS_PATH}: ${character.id} はどの科目からも使われていません`);
    }
  }
  return result();
}
